/**
 * Policies list — Filters drawer (OR-1664, design OR-1537).
 *
 * Per the Figma: Priority (checkbox chips), Status (the seven term
 * statuses), Inception Date (custom range), Expiration Date (Next 30 / 90 /
 * 180 days chips + custom range) and Brokerage (searchable select with no
 * visible label — the card title already says it — but an accessible name
 * for screen readers, per Andressa's note).
 *
 * Same behaviour as Submission Triage: edits stage a local draft; "Show
 * results" commits, "Clear All" resets. The filter model and the param
 * mapping live in `filters.ts`.
 */
import { useMemo, useState } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { parseDate } from "@internationalized/date";
import { X } from "@untitledui/icons";
import { Button, Checkbox, DateRangePicker, SearchSelect, cx } from "@tricura/atlas-ui";
import { POLICY_PRIORITIES, POLICY_TERM_STATUSES } from "../../api";
import type { PolicyPriority, PolicyTermStatus } from "../../api";
import { useBrokerageOptions } from "../../hooks/use-brokerage-options";
import {
    EMPTY_FILTERS,
    EXPIRATION_PRESETS,
    type ExpirationPreset,
    PRESET_LABELS,
    PRIORITY_LABELS,
    type PolicyFilters,
    STATUS_LABELS,
    countActiveFilters,
} from "./filters";

interface FiltersDrawerProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    value: PolicyFilters;
    onApply: (next: PolicyFilters) => void;
    onClearAll: () => void;
}

export function FiltersDrawer({ isOpen, onOpenChange, value, onApply, onClearAll }: FiltersDrawerProps) {
    return (
        <AriaModalOverlay
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            isDismissable
            className={({ isEntering, isExiting }) =>
                cx("bg-overlay/70 fixed inset-0 z-50", isEntering && "animate-in fade-in ease-out", isExiting && "animate-out fade-out ease-in")
            }
        >
            <AriaModal
                className={({ isEntering, isExiting }) =>
                    cx(
                        "bg-primary outline-hidden fixed bottom-0 right-0 top-0 w-[460px] max-w-full shadow-2xl",
                        isEntering && "animate-in slide-in-from-right ease-out",
                        isExiting && "animate-out slide-out-to-right ease-in",
                    )
                }
            >
                <AriaDialog aria-label="Filters" className="outline-hidden flex h-full flex-col">
                    {({ close }) => (
                        <DrawerBody
                            value={value}
                            onApply={(next) => {
                                onApply(next);
                                close();
                            }}
                            onClearAll={() => {
                                onClearAll();
                                close();
                            }}
                            close={close}
                        />
                    )}
                </AriaDialog>
            </AriaModal>
        </AriaModalOverlay>
    );
}

interface BodyProps {
    close: () => void;
    value: PolicyFilters;
    onApply: (next: PolicyFilters) => void;
    onClearAll: () => void;
}

function toggleIn<T>(list: T[], item: T): T[] {
    return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

function DrawerBody({ close, value, onApply, onClearAll }: BodyProps) {
    const [draft, setDraft] = useState<PolicyFilters>(value);
    // Same server-side search as Triage's Brokerage filter.
    const brokerageOptions = useBrokerageOptions(draft.brokerage);

    const update = <K extends keyof PolicyFilters>(key: K, val: PolicyFilters[K]) => {
        setDraft((prev) => ({ ...prev, [key]: val }));
    };

    // Preset and custom range write the same two params, so they are
    // mutually exclusive: setting one clears the other.
    const setPreset = (preset: ExpirationPreset) => {
        setDraft((prev) => ({
            ...prev,
            expirationPreset: prev.expirationPreset === preset ? "" : preset,
            expirationStart: "",
            expirationEnd: "",
        }));
    };
    const setExpirationRange = (start: string, end: string) => {
        setDraft((prev) => ({ ...prev, expirationPreset: "", expirationStart: start, expirationEnd: end }));
    };

    const hasFilters = countActiveFilters(draft) > 0;

    const inceptionValue = useMemo(
        () => (draft.inceptionStart && draft.inceptionEnd ? { start: parseDate(draft.inceptionStart), end: parseDate(draft.inceptionEnd) } : null),
        [draft.inceptionStart, draft.inceptionEnd],
    );
    const expirationValue = useMemo(
        () =>
            draft.expirationStart && draft.expirationEnd ? { start: parseDate(draft.expirationStart), end: parseDate(draft.expirationEnd) } : null,
        [draft.expirationStart, draft.expirationEnd],
    );

    return (
        <>
            <div className="border-secondary relative flex flex-col gap-0.5 border-b px-6 py-5">
                <h2 className="text-primary text-base font-semibold leading-6">Filters</h2>
                <p className="text-tertiary text-sm leading-5">Refine results across the table.</p>
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    data-autotest="policies-filters-button-close"
                    className="text-tertiary hover:text-primary size-8 hover:bg-secondary absolute right-3 top-3 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg"
                >
                    <X aria-hidden="true" className="size-5 stroke-[2.25px]" />
                </button>
            </div>

            <div className="atlas-scrollbar bg-secondary flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
                <Card title="Priority">
                    <div className="flex flex-wrap gap-2">
                        {POLICY_PRIORITIES.map((p: PolicyPriority) => (
                            <CheckboxChip
                                key={p}
                                label={PRIORITY_LABELS[p]}
                                checked={draft.priorities.includes(p)}
                                onChange={() => update("priorities", toggleIn(draft.priorities, p))}
                                dataAutotest={`policies-filters-checkbox-priority-${p}`}
                            />
                        ))}
                    </div>
                </Card>

                <Card title="Status">
                    <div className="flex flex-wrap gap-2">
                        {POLICY_TERM_STATUSES.map((st: PolicyTermStatus) => (
                            <CheckboxChip
                                key={st}
                                label={STATUS_LABELS[st]}
                                checked={draft.statuses.includes(st)}
                                onChange={() => update("statuses", toggleIn(draft.statuses, st))}
                                dataAutotest={`policies-filters-checkbox-status-${st}`}
                            />
                        ))}
                    </div>
                </Card>

                <Card title="Inception Date">
                    <Field label="Custom">
                        <DateRangePicker
                            aria-label="Inception date"
                            size="sm"
                            value={inceptionValue}
                            data-autotest="policies-filters-datepicker-inception-date"
                            onChange={(range) => {
                                update("inceptionStart", range?.start ? range.start.toString() : "");
                                update("inceptionEnd", range?.end ? range.end.toString() : "");
                            }}
                        />
                    </Field>
                </Card>

                <Card title="Expiration Date">
                    <div className="flex flex-wrap gap-2">
                        {EXPIRATION_PRESETS.map((preset) => (
                            <CheckboxChip
                                key={preset}
                                label={PRESET_LABELS[preset]}
                                checked={draft.expirationPreset === preset}
                                onChange={() => setPreset(preset)}
                                dataAutotest={`policies-filters-checkbox-expiration-${preset}`}
                            />
                        ))}
                    </div>
                    <Field label="Custom">
                        <DateRangePicker
                            aria-label="Expiration date"
                            size="sm"
                            value={expirationValue}
                            data-autotest="policies-filters-datepicker-expiration-date"
                            onChange={(range) =>
                                setExpirationRange(range?.start ? range.start.toString() : "", range?.end ? range.end.toString() : "")
                            }
                        />
                    </Field>
                </Card>

                <Card title="Brokerage">
                    {/* No visible label — the card title already names the
                      * field — but the select keeps an accessible name. */}
                    <SearchSelect
                        size="sm"
                        aria-label="Brokerage"
                        placeholder="Search and Select Brokerage"
                        data-autotest="policies-filters-select-brokerage"
                        items={brokerageOptions.items}
                        selectedKey={draft.brokerage?.id ?? null}
                        onSearchChange={brokerageOptions.onSearchChange}
                        onSelectionChange={(k) => update("brokerage", k ? (brokerageOptions.items.find((b) => b.id === k) ?? null) : null)}
                    >
                        {(item) => <SearchSelect.Item id={item.id}>{item.label}</SearchSelect.Item>}
                    </SearchSelect>
                </Card>
            </div>

            <div className="border-secondary bg-primary flex items-center justify-end gap-2 border-t px-6 py-4">
                <Button
                    color="secondary"
                    size="sm"
                    data-autotest="policies-filters-button-clear-all"
                    onClick={() => {
                        setDraft(EMPTY_FILTERS);
                        onClearAll();
                    }}
                >
                    Clear All
                </Button>
                <Button color="primary" size="sm" isDisabled={!hasFilters} onClick={() => onApply(draft)} data-autotest="policies-filters-button-apply">
                    Show results
                </Button>
            </div>
        </>
    );
}

// ─────────────────────── Reusable primitives ───────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="border-secondary bg-primary flex flex-col gap-3 rounded-xl border p-4">
            <h3 className="text-primary text-sm font-semibold">{title}</h3>
            {children}
        </section>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-secondary text-sm font-medium">{label}</span>
            {children}
        </div>
    );
}

function CheckboxChip({ label, checked, onChange, dataAutotest }: { label: string; checked: boolean; onChange: () => void; dataAutotest?: string }) {
    return (
        <label
            data-autotest={dataAutotest}
            className={cx(
                "border-secondary bg-primary inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-md border pl-1 pr-2 text-xs font-medium transition duration-100 ease-linear",
                checked ? "border-brand text-primary" : "text-secondary hover:bg-secondary",
            )}
        >
            <Checkbox isSelected={checked} onChange={onChange} aria-label={label} />
            {label}
        </label>
    );
}
